import { useState } from 'react';
import type { CartLine } from '../lib/cart';
import { formatPrice, subtotalCents } from '../lib/cart';
import type { SlotConfig, TimeSlot } from '../lib/slots';
import SlotPicker from './SlotPicker';

/**
 * Page dédiée « Heure de retrait » (parité legacy, 2026-06-24) — étape PLEINE entre le
 * panier et la finalisation. Le client choisit son créneau sur un écran sans distraction
 * (cohérent mobile, comme Uber Eats), puis renseigne coordonnées + paiement à l'étape suivante.
 * Aucune logique de commande ici : le créneau choisi est remonté tel quel à `Checkout`.
 */
interface SlotPageProps {
  lines: CartLine[];
  slotConfig: SlotConfig;
  currency?: 'EUR' | 'CHF';
  phone?: string | null;
  /** Livraison activée côté resto → titre « … ou de livraison » (parité §F-20). */
  deliveryEnabled?: boolean;
  /** Créneau déjà choisi (retour depuis le checkout via « Modifier »). */
  initial?: TimeSlot | null;
  onBack: () => void;
  onContinue: (slot: TimeSlot) => void;
}

export default function SlotPage({
  lines,
  slotConfig,
  currency = 'EUR',
  phone,
  deliveryEnabled = false,
  initial = null,
  onBack,
  onContinue,
}: SlotPageProps) {
  const [picked, setPicked] = useState<TimeSlot | null>(initial);
  const title = deliveryEnabled ? 'Heure de retrait ou de livraison' : 'Heure de retrait';
  const cardClass = 'rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5';

  return (
    <section className="w-full">
      <button onClick={onBack} className="mb-2 text-sm font-semibold text-slate-500 hover:text-slate-800">
        ← Retour au panier
      </button>
      <h1 className="display-font mb-1 text-3xl font-black sm:text-4xl">{title}</h1>
      <p className="mb-5 text-sm text-slate-500">
        Choisissez le moment qui vous arrange. Vous renseignerez vos coordonnées et le paiement à l'étape suivante.
      </p>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Sélecteur de créneau (pleine largeur de la colonne) */}
        <div className={cardClass}>
          <SlotPicker config={slotConfig} value={picked} onSelect={setPicked} phone={phone} title={title} />
        </div>

        {/* Récap collant + bouton continuer */}
        <aside className="h-fit lg:sticky lg:top-24">
          <div className={cardClass}>
            <h3 className="mb-3 text-lg font-extrabold">Votre commande</h3>
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>{lines.length} article{lines.length > 1 ? 's' : ''}</span>
              <span className="font-semibold">{formatPrice(subtotalCents(lines), currency)}</span>
            </div>
            {picked && (
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
                ✅ Créneau : {picked.label}
                {picked.dayLabel ? ` · ${picked.dayLabel}` : ''}
              </div>
            )}
            <button
              disabled={!picked}
              onClick={() => picked && onContinue(picked)}
              className="mt-4 w-full rounded-xl py-3.5 text-base font-bold text-white shadow-lg transition active:scale-95 disabled:opacity-40"
              style={{ background: 'var(--brand-primary)' }}
            >
              Continuer →
            </button>
            {!picked && (
              <p className="mt-2 text-center text-xs text-slate-400">Sélectionnez un créneau pour continuer.</p>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
